import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PackageService } from '../../package-center/package/package.service';
import { DtoMetric, DtoModule, DtoPackage, DtoPreviewItem, DtoSubCategory, PackagePayload } from '../../../models/package-center.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { DtoSchool, TransferSchool } from '../../../models/school.model';
import { SchoolService } from '../../school/school.service';
import { ClientAccountDetails, Pagination } from '../../../models/mat-account.model';
import { MatAccountService } from '../../mat-account/mat-account.service';
import { PackageType } from '../../package-center/package/package.component';
import { ProdetailsModalComponent } from '../../data-management/prodetails-modal/prodetails-modal.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-transfer-school',
  templateUrl: './transfer-school.component.html'
})
export class TransferSchoolComponent implements OnInit {

  clientAccountId: number = 0;
  clientAccountName: string = "";
  packageId: number = 0;
  schoolList: DtoSchool[] = [];
  matAccountList: ClientAccountDetails[] = [];
  packageList: DtoPackage[] = [];
  pagination: Pagination = {
    searchTerm: "",
    pageOffset: 0,
    pageSize: 0,
    sortColumn: "",
    sortDirection: "",
    userRole: [] as string[],
    isActive: true
  }
  isEditPackage: boolean = false;
  selectedPackage: string = "";
  packageDetails: DtoPackage = {} as DtoPackage;
  userRole: string[] = [];
  shouldOpen: boolean = true;
  independentSchoolData: boolean = false;
  schoolId: number = 0;
  schoolName: string = "";
  numberOfUsersToAllow: number = 0;

  subCategories: DtoSubCategory[] = [];
  subCategoriesForCreate: DtoSubCategory[] = [];

  groupedSubCategories: { [key: string]: DtoSubCategory[] } = {};
  selectedGroupedSubcategories: {[key:string]: DtoSubCategory[]}={};
  packageControl = Controls;

  transferSchoolForm: FormGroup = new FormGroup({
    existingMatAccountName: new FormControl(''),
    school: new FormControl(null as number | null, [Validators.required]),
    newMatAccount: new FormControl(null as number | null, [Validators.required]),
    package: new FormControl(null as number | null, [Validators.required]),
    newPackageName: new FormControl(''),
    modules: new FormControl([]),
    subCategories: new FormControl([])
  });

  constructor(private packageService: PackageService, private toasterService: ToastrService, private router: Router,
    private schoolService: SchoolService, private matAccountService: MatAccountService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.transferSchoolForm.controls['newMatAccount'].disable();
    this.userRole = sessionStorage.getItem('user_role')?.split(',')!;
    const state = history.state as
      {
        clientAccountId: number,
        clientAccountName: string,
        packageId: number,
        independentSchoolData: boolean,
        schoolId: number,
        schoolName: string,
        numberOfUsersToAllow: number
      };
    if (state) {
      this.clientAccountId = state.clientAccountId;
      this.clientAccountName = state.clientAccountName ? state.clientAccountName : 'Individual School';
      this.packageId = state.packageId;
      if (this.clientAccountId == 0) {
        this.independentSchoolData = state.independentSchoolData;
        this.schoolId = state.schoolId;
        this.schoolName = state.schoolName;
        this.numberOfUsersToAllow = state.numberOfUsersToAllow;
      }
    }
    this.getMatAccount(this.pagination);
    if (this.clientAccountId > 0) {
      this.getSchoolsUnderClientAccount(this.clientAccountId);
    }
    else {
      this.getIndependentSchools(this.pagination);
    }
    this.getPackages();
    this.watchSchoolControl();
  }

  watchSchoolControl(): void {
    this.transferSchoolForm.get('school')?.valueChanges.subscribe(value => {
      if (value <= 0) {
        this.transferSchoolForm.controls['newMatAccount'].disable();
      }
      else {
        this.transferSchoolForm.controls['newMatAccount'].enable();
      }
    });
  }

  editPackage() {
    if (this.transferSchoolForm.controls['package'].value <= 0) {
      this.toasterService.warning("Please select a package to proceed!");
      return;
    }
    if (this.isEditPackage) {
      this.undoPackageCustomization();
    }
    else {
      this.isEditPackage = true;
      this.transferSchoolForm.controls['newPackageName'].setValidators([Validators.required, Validators.pattern("^.*[a-zA-Z]+.*$ ?")]);
      this.transferSchoolForm.controls['modules'].setValidators([Validators.required]);
      this.transferSchoolForm.controls['subCategories'].setValidators([Validators.required]);
      this.transferSchoolForm.updateValueAndValidity();
    }
  }

  undoPackageCustomization() {
    this.transferSchoolForm.controls['newPackageName'].setValue('');
    this.transferSchoolForm.controls['newPackageName'].removeValidators([Validators.required]);
    this.transferSchoolForm.controls['newPackageName'].setErrors(null);

    this.transferSchoolForm.controls['modules'].setValue([]);
    this.transferSchoolForm.controls['modules'].removeValidators([Validators.required]);
    this.transferSchoolForm.controls['modules'].setErrors(null);

    this.transferSchoolForm.controls['subCategories'].setValue([]);
    this.transferSchoolForm.controls['subCategories'].removeValidators([Validators.required]);
    this.transferSchoolForm.controls['subCategories'].setErrors(null);

    this.transferSchoolForm.markAsDirty();
    this.transferSchoolForm.updateValueAndValidity();
    this.isEditPackage = false;
  }

  getPackages() {
    this.packageService.getPackages(1).subscribe((response: DtoPackage[] | any) => {
      this.packageList = response;
      this.packageDetails = this.packageList.find(x => x.packageId == this.packageId)!;
    }, (err: HttpErrorResponse) => {
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    });
  }

  onSelectNewMatAccount() {
    if (this.transferSchoolForm.controls['school'].value <= 0) {
      this.toasterService.warning("Please select a school to transfer");
    }
  }

  transferSchool() {
    if (this.transferSchoolForm.invalid || this.transferSchoolForm.untouched) {
      this.toasterService.warning("Please fill all required fields");
      this.transferSchoolForm.markAllAsTouched();
      return;
    }
    if (this.isEditPackage) { //Customizations done
      //Create Package
      let packageDetails: PackagePayload = {
        packageName: this.transferSchoolForm.controls['newPackageName'].value,
        modules: this.transferSchoolForm.controls['modules'].value,
        subCategories: this.transferSchoolForm.controls['subCategories'].value,
        packageType: PackageType.RequestedToCreateNewPackage
      } as unknown as PackagePayload;
      this.packageService.createPackage(packageDetails).subscribe(response => {
        if (response) {
          this.raiseSchoolTransferRequest(this.isEditPackage);
        }
      }, (error: HttpErrorResponse) => {
        if(error.error){
          this.toasterService.error(error.error, "Error");
        }
      })
    }
    else {
      //Swap Package
      this.raiseSchoolTransferRequest(this.isEditPackage);
    }
  }

  raiseSchoolTransferRequest(isEditPackage: boolean) {
    let packageName = isEditPackage ? this.transferSchoolForm.controls['newPackageName'].value :
      this.packageList.find(x => x.packageId == this.transferSchoolForm.controls['package'].value)?.packageName
    let transferSchoolPayload = {
      transferSchoolTypeId: this.transferSchoolForm.controls['newMatAccount'].value == -1 ?
        TransferSchoolType.TransferFromClientAccountToIndividualSchool :
        (this.clientAccountId > 0 ? TransferSchoolType.TransferFromClientAccountToAnotherClientAccount : TransferSchoolType.TransferFromIndividualSchoolToClientAccount),
      schoolToTransfer: this.transferSchoolForm.controls['school'].value,
      transferredFrom: this.clientAccountId,
      transferredTo: this.transferSchoolForm.controls['newMatAccount'].value,
      packageChangeRequestTypeId: isEditPackage ? PackageType.RequestedToCreateNewPackage : PackageType.RequestToUpgrade,
      packageName: packageName
    } as TransferSchool
    this.schoolService.raiseSchoolTransferRequest(transferSchoolPayload, this.transferSchoolForm.controls['school'].value).subscribe(response => {
      if (response) {
        this.toasterService.success("School transfer process initiated successfully!");
        this.transferSchoolForm.reset();
        this.isEditPackage = false;
        this.navigateToUserCenter();
      }
    }, (error: HttpErrorResponse) => {
      if(error.error){
        this.toasterService.error(error.error, "Error");
      }
    })
  }

  getMatAccount(payload: Pagination) {
    payload.searchTerm = "";
    payload.userRole = this.userRole;
    let sectorId = Number(sessionStorage.getItem('sector'));
    this.matAccountService.getMatAccount(sectorId, payload).subscribe((response: ClientAccountDetails[] | any) => {
      this.matAccountList = response.filter((x: ClientAccountDetails) => x.clientAccountId != this.clientAccountId);
      if (this.clientAccountId > 0) { //It is not necessary when an individual school is transferred
        this.matAccountList.unshift({
          clientAccountId: -1,
          clientAccountName: "Individual School"
        } as ClientAccountDetails);
      }
      this.transferSchoolForm.controls['existingMatAccountName'].setValue(this.clientAccountName);
    }, (error: HttpErrorResponse) => {
      if(error.error){
        this.toasterService.error(error.error, "Error");
      }
    })
  }

  getIndependentSchools(payload: Pagination) {
    this.schoolService.getIndependentSchools(payload).subscribe((response: DtoSchool[]) => {
      this.schoolList = response;
    }, (error: HttpErrorResponse) => {
      if(error.error){
        this.toasterService.error(error.error, "Error");
      }
    })
  }

  getSchoolsUnderClientAccount(clientAccountId: number) {
    this.schoolService.getSchools(clientAccountId).subscribe((data: DtoSchool[] | any) => {
      this.schoolList = data;
    }, (error: HttpErrorResponse) => {
      if(error.error){
        this.toasterService.error(error.error, "Error");
      }
    })
  }

  onSelectingAccountToTransfer(selectedValue: number) { //selectedValue == -1 -> Independent School
    /*
    Mat Acc --> Ind Sch : Allow to customize package & change to other package
    Ind Sch --> Mat Acc-1 : By default select Mat Acc-1 Package 
                            (Allow only to customize Mat Acc-1 package)
    Mat Acc-1 --> Mat Acc-2 : By default select Mat Acc-2 Package 
                              (Allow only to customize Mat Acc-2 package)
    */
    this.undoPackageCustomization();
    if (selectedValue != -1) { //Transfer to another mat account
      let packageId = this.matAccountList.find(x => x.clientAccountId == selectedValue)?.packageId;
      this.transferSchoolForm.controls['package'].setValue(packageId);
      this.selectedPackage = this.packageList.find(p => p.packageId === packageId)?.packageName!;
      this.packageDetails = this.packageList.find(x=>x.packageId == packageId)!;
      this.subCategories = [];
      this.selectedGroupedSubcategories = this.generateSelectedCategoryModules(this.subCategories);
      this.groupedSubCategories = this.generateSelectedCategoryModules(this.subCategories);
      this.transferSchoolForm.controls['package'].disable();
    }
    else {
      this.transferSchoolForm.controls['package'].setValue(null);
      this.transferSchoolForm.controls['package'].enable();
    }
  }

  
  removeModule(moduleToRemove: DtoModule) {
    this.transferSchoolForm.controls['modules'].setValue(this.transferSchoolForm.controls['modules'].value.filter((x: DtoModule) => x.moduleId != moduleToRemove.moduleId));
    this.onModuleChange({ value: this.transferSchoolForm.controls['modules'].value });
  }
  
  onPackageChange(packageId: number) {
    this.selectedPackage = this.packageList.find(p => p.packageId === packageId)?.packageName!;
    this.packageDetails = this.packageList.find(x => x.packageId == packageId)!;
    this.subCategories = [];
    this.undoPackageCustomization();
    this.selectedGroupedSubcategories = this.generateSelectedCategoryModules(this.subCategories);
    this.groupedSubCategories = this.generateSelectedCategoryModules(this.subCategories);
  }

  onModuleChange(selectedModule: any) {
    let payload = selectedModule.value;
    this.packageService.getSubCategories(payload).subscribe((response: DtoSubCategory[] | any) => {
      this.subCategories = response;

      let subCategoryList = this.subCategories.filter(subCategory => {
        return this.transferSchoolForm.controls['subCategories'].value?.some((packagesubCategory: DtoSubCategory) => subCategory.subCategoryId === packagesubCategory.subCategoryId);
      });

      this.transferSchoolForm.controls['subCategories'].setValue(subCategoryList);

      this.selectedGroupedSubcategories = this.generateSelectedCategoryModules(subCategoryList);
      this.groupedSubCategories = this.generateSelectedCategoryModules(this.subCategories);
    }, (err: HttpErrorResponse) => {
      if(err.error){
        this.toasterService.error(err.error, "Error");
      }
    })
  }

    
  previewPopup(title: string, control: string, packageDetails: DtoPackage = {} as DtoPackage) 
  {
    
    this.dialog.open(ProdetailsModalComponent, {
      width: '1000px',
      panelClass: 'modal-prodetails',
      data: {
        singleItemPreview: title ? true : false,
        packageDetails: packageDetails,
        itemToPreview: this.getPreviewData(control),
        title: title ? title : (packageDetails.packageName??'New') + " Details"
      }
    })
  }
  
  getPreviewData(control: string):DtoPreviewItem[] 
  {
      switch (control) {
        case this.packageControl.Module:
          return  this.transferSchoolForm.controls['modules'].value.map((x: DtoModule) => ({
              groupName: null,
              itemName: x.moduleName
            }));
        case this.packageControl.SubCategory:
          return this.transferSchoolForm.controls['subCategories'].value.map((x: DtoSubCategory) => ({
              groupName: x.moduleDisplayName,
              itemName: x.subCategoryName,
              itemDisplay: x.subCategoryDisplayName
            } as DtoPreviewItem));
  
        case this.packageControl.Metrics:
          return this.transferSchoolForm.controls['metrics'].value.map((x: DtoMetric) => ({
              groupName: null,
              itemName: x.metricName
            }));
        default:
          return [];
      }
  }
      
  navigateToAdminDetails() {
    this.transferSchoolForm.reset();
    let queryParams: any = {
      clientAccountId: this.clientAccountId,
      clientAccountName: this.clientAccountId > 0 ? this.clientAccountName : '',
      packageId: this.packageId
    }
    if (this.clientAccountId == 0) {
      queryParams = {
        ...queryParams,
        independentSchoolData: 1,
        schoolId: this.schoolId,
        schoolName: this.schoolName,
        numberOfUsersToAllow: this.numberOfUsersToAllow
      }
    }
    this.router.navigate(['/adminDetails'],
      {
        queryParams: queryParams,
        queryParamsHandling: 'merge'
      });
  }

  navigateToUserCenter() {
    this.transferSchoolForm.reset();
    this.router.navigate(['/userCenter']);
  }

  generateSelectedCategoryModules(selectedValaues: DtoSubCategory[] )
  {
    if (!selectedValaues || selectedValaues.length === 0) {
      return {};
      }

    
    const groupedValues = selectedValaues.reduce((result, sc) => 
            {
              const key = sc.moduleDisplayName!;
              result[key] = result[key] || [];
              result[key].push(sc);
              return result;
            },{} as {[key:string]: DtoSubCategory[]});

    return groupedValues;
  }

}

export enum TransferSchoolType { TransferFromClientAccountToIndividualSchool = 1, TransferFromIndividualSchoolToClientAccount = 2, TransferFromClientAccountToAnotherClientAccount = 3 }
export enum Controls { Package="packages", Module = "modules", SubCategory = "subCategories", Metrics = "metrics"}
